import { DEFAULT_WISDOM_SUMMARY_TIMEOUT } from '@modules/common/constants';
import { useCaptureSummaryMessage } from '@modules/oracle/hooks/useCaptureSummaryMessage';
import { useCreateWebSocketConnection } from '@modules/oracle/hooks/useCreateWebSocketConnection';
import { useInitiateGenerateFeedbackSummary } from '@modules/oracle/hooks/useInitiateGenerateFeedbackSummary';
import { captureMessage } from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import ErrorCode from 'v2/core/errors/codes';

interface useGetFeedbackListSummaryByEqlParams {
  eql: string | undefined;
  shouldGetSummaryOnDemand?: boolean;
  prompt?: string;
  timeoutDuration?: number;
}

const MAX_RETRY_COUNT = 3;

export function useGetFeedbackListSummaryByEql({
  eql,
  shouldGetSummaryOnDemand: shouldGetSummaryOnDemandProp = false,
  prompt: promptProp,
  timeoutDuration = DEFAULT_WISDOM_SUMMARY_TIMEOUT,
}: useGetFeedbackListSummaryByEqlParams) {
  const [prompt, setPrompt] = useState(promptProp);
  const [shouldGetSummaryOnDemand, setShouldGetSummaryOnDemand] = useState(
    shouldGetSummaryOnDemandProp,
  );

  const [isRequestTimedOut, setIsRequestTimedOut] = useState(false);
  const [retryCount, setRetryCount] = useState(MAX_RETRY_COUNT);

  const [createWebSocketConnection, webSocketConnectionOpts] =
    useCreateWebSocketConnection();

  const [
    initiateGenerateFeedbackSummary,
    { error, isLoading, responseID: responseIDFromFeedbackSummary },
  ] = useInitiateGenerateFeedbackSummary({
    onCompleted: (responseID) => {
      // Reset the retry count to its default value
      if (responseID) {
        setRetryCount(MAX_RETRY_COUNT);
      }
    },
  });

  const [captureSummaryMessage, { messages }] = useCaptureSummaryMessage({
    responseID: responseIDFromFeedbackSummary,
    sessionID: webSocketConnectionOpts.sessionID,
  });

  useEffect(() => {
    const disconnect = createWebSocketConnection();

    return disconnect;
  }, [createWebSocketConnection]);

  const { webSocket, sessionID, closeWebSocketConnection } =
    webSocketConnectionOpts;

  useDebounce(
    () => {
      if (!eql || !sessionID) {
        return;
      }

      if (shouldGetSummaryOnDemand) {
        return;
      }

      initiateGenerateFeedbackSummary({
        eql,
        sessionID,
        prompt,
      });
    },
    200,
    [
      eql,
      initiateGenerateFeedbackSummary,
      sessionID,
      shouldGetSummaryOnDemand,
      prompt,
    ],
  );

  const dependencies = {
    messages,
    webSocket,
    eql,
    prompt,
    sessionID,
    timeoutDuration,
    retryCount,
  };

  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  useEffect(() => {
    setIsRequestTimedOut(false);

    if (!dependenciesRef.current.webSocket || !responseIDFromFeedbackSummary) {
      return;
    }

    captureSummaryMessage({ webSocket: dependenciesRef.current.webSocket });
    const timer = setTimeout(() => {
      if (!dependenciesRef.current.messages.length) {
        setIsRequestTimedOut(true);
        closeWebSocketConnection(dependenciesRef.current.webSocket);
        captureMessage('Failed to fetch websocket response within 1 minute', {
          extra: {
            responseID: responseIDFromFeedbackSummary,
            eql: dependenciesRef.current.eql,
            prompt: dependenciesRef.current.prompt,
            sessionID: dependenciesRef.current.sessionID,
          },
          level: 'info',
        });
      }
    }, dependenciesRef.current.timeoutDuration);

    return () => {
      clearTimeout(timer);
      setIsRequestTimedOut(false);
    };
  }, [
    closeWebSocketConnection,
    captureSummaryMessage,
    responseIDFromFeedbackSummary,
  ]);

  const getFeedbackSummary = useCallback(
    (prompt?: string) => {
      if (!eql || !sessionID) {
        return;
      }

      setPrompt(prompt);
      return initiateGenerateFeedbackSummary({
        eql,
        sessionID,
        prompt,
      });
    },
    [eql, initiateGenerateFeedbackSummary, sessionID],
  );

  const reopenWebsocketConnection = useCallback(() => {
    if (!webSocket) {
      return;
    }

    setIsRequestTimedOut(false);
    closeWebSocketConnection(webSocket);
    createWebSocketConnection();
  }, [closeWebSocketConnection, createWebSocketConnection, webSocket]);

  useEffect(() => {
    if (
      error?.graphQLErrors?.[0]?.extensions.errCode ===
        ErrorCode.UnprocessableEntity &&
      dependenciesRef.current.retryCount > 0
    ) {
      setShouldGetSummaryOnDemand(false);
      reopenWebsocketConnection();
      setRetryCount((count) => count - 1);
      captureMessage('Lost websocket connection for CWF', {
        extra: {
          retryCount: dependenciesRef.current.retryCount,
          eql: dependenciesRef.current.eql,
          prompt: dependenciesRef.current.prompt,
          sessionID: dependenciesRef.current.sessionID,
        },
        level: 'info',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return {
    responseID: responseIDFromFeedbackSummary,
    getFeedbackSummary,
    sessionID,
    reopenWebsocketConnection,
    error,
    isRequestTimedOut,
    isLoading,
  } as const;
}
